import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { SignupRequest } from "../../domain/types/auth";
import { useAuthQueries } from "../hooks/use-auth-queries";
import { Button } from "../components/UI/Button";
import { Input } from "../components/UI/Input";
import { Pod24CommonLayout } from "../layouts/Pod24CommonLayout";
import type { SocialProvider } from "shared/types/api";

// 이메일 회원가입 스키마
const EmailSignupSchema = z
  .object({
    email: z.string().email("유효한 이메일을 입력해주세요"),
    name: z.string().min(1, "이름을 입력해주세요"),
    password: z
      .string()
      .min(8, "비밀번호는 8자 이상이어야 합니다")
      .max(16, "비밀번호는 16자 이하여야 합니다")
      .regex(/[a-zA-Z]/, "영문자를 포함해야 합니다")
      .regex(/[0-9]/, "숫자를 포함해야 합니다")
      .regex(
        /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/,
        "특수문자를 포함해야 합니다",
      )
      .regex(
        /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/,
        "영문, 숫자, 특수문자만 입력 가능합니다",
      ),
    confirmPassword: z.string(),
    agreementTerms: z.boolean().refine((val) => val === true, {
      message: "서비스 이용약관에 동의해주세요",
    }),
    agreementPrivacy: z.boolean().refine((val) => val === true, {
      message: "개인정보 수집및 이용에 동의해주세요",
    }),
    agreementAge: z.boolean().refine((val) => val === true, {
      message: "만 14세 이상입니다에 동의해주세요",
    }),
    agreementMarketing: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다",
    path: ["confirmPassword"],
  });

type EmailSignupFormData = {
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
  agreementTerms: boolean;
  agreementPrivacy: boolean;
  agreementAge: boolean;
  agreementMarketing?: boolean;
};

// 소셜 로그인 + 이메일. 기존 SocialProvider 타입을 재활용.
export type LoginMethod = SocialProvider | "EMAIL";

export const LOGIN_METHOD_LABELS: Record<LoginMethod, string> = {
  EMAIL: "이메일",
  KAKAO: "카카오",
  NAVER: "네이버",
  GOOGLE: "구글",
};

// 코드를 한글 명칭으로 변환. 매핑에 없으면 원본 코드를 반환.
export const getLoginMethodLabel = (method: string): string =>
  LOGIN_METHOD_LABELS[method as LoginMethod] || method;

const buildExistingAccountMessage = (loginMethods?: string[]): string => {
  if (Array.isArray(loginMethods) && loginMethods.length > 0) {
    const labels = loginMethods.map(getLoginMethodLabel).join(", ");
    return `이미 ${labels} 계정으로 가입된 이메일입니다. ${labels} 로그인을 이용해 주세요.`;
  }
  return "이메일이 이미 존재합니다.";
};

export const EmailSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signupMutation } = useAuthQueries();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EmailSignupFormData>({
    resolver: zodResolver(EmailSignupSchema),
    defaultValues: {
      agreementTerms: false,
      agreementPrivacy: false,
      agreementAge: false,
      agreementMarketing: false,
    },
  });

  // 폼 값 감시
  const agreementValues = watch([
    "agreementTerms",
    "agreementPrivacy",
    "agreementAge",
    "agreementMarketing",
  ]);

  // 전체 선택 핸들러
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setValue("agreementTerms", checked);
    setValue("agreementPrivacy", checked);
    setValue("agreementAge", checked);
    setValue("agreementMarketing", checked);
  };

  // 개별 체크박스 상태에 따른 전체 선택 상태 업데이트
  React.useEffect(() => {
    const [terms, privacy, age, marketing] = agreementValues;
    const allRequired = terms && privacy && age;
    const allChecked = allRequired && (marketing ?? false);
    setSelectAll(allChecked);
  }, [agreementValues]);

  const onSubmit = async (data: EmailSignupFormData) => {
    try {
      const signupData: SignupRequest = {
        email: data.email,
        password: data.password,
        name: data.name,
        termsOfServiceAgreed: data.agreementTerms,
        privacyPolicyAgreed: data.agreementPrivacy,
        over14YearsOld: data.agreementAge,
        promotionNewsSubscribed: data.agreementMarketing || false,
      };
      await signupMutation.mutateAsync(signupData);
      // 회원가입 완료 후 이메일 인증 안내 페이지로 이동
      navigate("/?globalModalMessage=회원가입이 완료되었습니다. 이메일을 확인하여 인증을 완료해 주세요.");
    } catch (error) {
      console.error("Signup error:", error);
    }
  };

  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (signupMutation.error) {
      const error = signupMutation.error as any;
      const original = error?.originalError ?? error;
      const errorCode = original?.errorCode;
      if (
        errorCode === 2002 ||
        error?.message?.includes("User already exists")
      ) {
        return setErrorMessage(
          buildExistingAccountMessage(original?.loginMethods),
        );
      }

      setErrorMessage("회원가입에 실패하였습니다.");
    }
  }, [signupMutation.error]);
  return (
    <Pod24CommonLayout>
      <div className="flex justify-center px-4" style={{ paddingTop: "100px" }}>
        <div className="w-full" style={{ maxWidth: "560px" }}>
          <>
            {/* 에러 메시지 */}
            {errorMessage && (
              <div>
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-sm text-red-600">{errorMessage}</div>
                </div>
                <div className="mb-8"></div>
              </div>
            )}
          </>

          {/* 제목 */}
          <div className="text-left" style={{ marginBottom: "36px" }}>
            <h1
              className="font-['Pretendard'] font-normal text-gray-900"
              style={{ fontSize: "24px" }}
            >
              회원가입 하고
            </h1>
            <h2
              className="font-['Pretendard'] font-semibold text-gray-900"
              style={{ fontSize: "24px" }}
            >
              서비스를 이용하세요.
            </h2>
          </div>

          {/* 구분선 */}
          <div
            className="w-full border-t border-gray-300"
            style={{ marginBottom: "16px" }}
          ></div>

          <form className="space-y-0" onSubmit={handleSubmit(onSubmit)}>
            <>
              {/* 이메일 */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  className="block text-left font-['Pretendard'] font-medium text-gray-900"
                  style={{ fontSize: "16px", marginBottom: "12px" }}
                >
                  이메일
                </label>
                <Input
                  type="email"
                  placeholder="이메일을 입력해 주세요."
                  autoComplete="email"
                  {...register("email")}
                  error={errors.email?.message}
                  className="w-full"
                  style={{
                    width: "100%",
                    maxWidth: "560px",
                    height: "40px",
                    borderRadius: "8px",
                  }}
                />
              </div>

              {/* 이름/업체명 */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  className="block text-left font-['Pretendard'] font-medium text-gray-900"
                  style={{ fontSize: "16px", marginBottom: "12px" }}
                >
                  이름/업체명
                </label>
                <Input
                  type="text"
                  placeholder="이름 또는 업체명을 입력해 주세요."
                  autoComplete="name"
                  {...register("name")}
                  error={errors.name?.message}
                  className="w-full"
                  style={{
                    width: "100%",
                    maxWidth: "560px",
                    height: "40px",
                    borderRadius: "8px",
                  }}
                />
              </div>

              {/* 비밀번호 */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  className="block text-left font-['Pretendard'] font-medium text-gray-900"
                  style={{ fontSize: "16px", marginBottom: "12px" }}
                >
                  비밀번호
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="영문 소문자 특수문자 조합 8-16자리"
                    autoComplete="new-password"
                    {...register("password")}
                    error={errors.password?.message}
                    className="w-full pr-16"
                    style={{
                      width: "100%",
                      maxWidth: "560px",
                      height: "40px",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 font-['Pretendard'] font-medium text-gray-500 hover:text-gray-700"
                    style={{ fontSize: "14px" }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "숨기기" : "보기"}
                  </button>
                </div>
              </div>

              {/* 비밀번호 확인 */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  className="block text-left font-['Pretendard'] font-medium text-gray-900"
                  style={{ fontSize: "16px", marginBottom: "12px" }}
                >
                  비밀번호 확인
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="비밀번호를 다시 입력해 주세요."
                    autoComplete="new-password"
                    {...register("confirmPassword")}
                    error={errors.confirmPassword?.message}
                    className="w-full pr-16"
                    style={{
                      width: "100%",
                      maxWidth: "560px",
                      height: "40px",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 font-['Pretendard'] font-medium text-gray-500 hover:text-gray-700"
                    style={{ fontSize: "14px" }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "숨기기" : "보기"}
                  </button>
                </div>
              </div>

              {/* 약관 동의 */}
              <div style={{ marginBottom: "40px" }}>
                {/* 전체 선택 체크박스 */}
                <label className="flex items-center mb-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="sr-only"
                    />
                    <img
                      src={
                        selectAll
                          ? "/assets/icons/checkbox-all-checked.svg"
                          : "/assets/icons/checkbox-all-unchecked.svg"
                      }
                      alt="checkbox"
                      className="w-6 h-6"
                    />
                  </div>
                  <span
                    className="ml-2 font-['Pretendard'] font-medium text-gray-700"
                    style={{ fontSize: "14px" }}
                  >
                    전체 선택
                  </span>
                </label>

                <div className="text-left space-y-3 ml-6">
                  {/* 필수 약관들 */}
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        {...register("agreementTerms")}
                        className="sr-only"
                      />
                      <img
                        src={
                          watch("agreementTerms")
                            ? "/assets/icons/checkbox-checked.svg"
                            : "/assets/icons/checkbox-unchecked.svg"
                        }
                        alt="checkbox"
                        className="w-6 h-6"
                      />
                    </div>
                    <span
                      className="ml-2 font-['Pretendard'] font-medium text-gray-700"
                      style={{ fontSize: "14px" }}
                    >
                      (필수) 서비스 이용약관 동의
                    </span>
                  </label>
                  {errors.agreementTerms && (
                    <p className="text-red-600 text-sm ml-6">
                      {errors.agreementTerms.message}
                    </p>
                  )}

                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        {...register("agreementPrivacy")}
                        className="sr-only"
                      />
                      <img
                        src={
                          watch("agreementPrivacy")
                            ? "/assets/icons/checkbox-checked.svg"
                            : "/assets/icons/checkbox-unchecked.svg"
                        }
                        alt="checkbox"
                        className="w-6 h-6"
                      />
                    </div>
                    <span
                      className="ml-2 font-['Pretendard'] font-medium text-gray-700"
                      style={{ fontSize: "14px" }}
                    >
                      (필수) 개인정보 수집 및 이용 동의
                    </span>
                  </label>
                  {errors.agreementPrivacy && (
                    <p className="text-red-600 text-sm ml-6">
                      {errors.agreementPrivacy.message}
                    </p>
                  )}

                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        {...register("agreementAge")}
                        className="sr-only"
                      />
                      <img
                        src={
                          watch("agreementAge")
                            ? "/assets/icons/checkbox-checked.svg"
                            : "/assets/icons/checkbox-unchecked.svg"
                        }
                        alt="checkbox"
                        className="w-6 h-6"
                      />
                    </div>
                    <span
                      className="ml-2 font-['Pretendard'] font-medium text-gray-700"
                      style={{ fontSize: "14px" }}
                    >
                      (필수) 만 14세 이상입니다
                    </span>
                  </label>
                  {errors.agreementAge && (
                    <p className="text-red-600 text-sm ml-6">
                      {errors.agreementAge.message}
                    </p>
                  )}

                  {/* 선택 약관 */}
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        {...register("agreementMarketing")}
                        className="sr-only"
                      />
                      <img
                        src={
                          watch("agreementMarketing")
                            ? "/assets/icons/checkbox-checked.svg"
                            : "/assets/icons/checkbox-unchecked.svg"
                        }
                        alt="checkbox"
                        className="w-6 h-6"
                      />
                    </div>
                    <span
                      className="ml-2 font-['Pretendard'] font-medium text-gray-700"
                      style={{ fontSize: "14px" }}
                    >
                      (선택) 이벤트, 혜택, 신제품 소식 제공 받기
                    </span>
                  </label>
                </div>
              </div>

              {/* 회원가입 버튼 */}
              <Button
                type="submit"
                loading={signupMutation.isPending}
                className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-['Pretendard'] font-semibold transition-colors"
                style={{
                  fontSize: "16px",
                  width: "100%",
                  maxWidth: "560px",
                  height: "48px",
                  borderRadius: "8px",
                  marginBottom: "118px",
                }}
              >
                회원 가입하기
              </Button>
            </>
          </form>
        </div>
      </div>
    </Pod24CommonLayout>
  );
};
